"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { FaTrash } from 'react-icons/fa';
import { Search, ChevronDown } from "lucide-react";
import styles from "./css/create-script.module.css"
import Pagination from "../../../../components/Pagination"
import Button from "../../../../components/Button"
import Checkbox from "../../../../components/Checkbox"
import SmallerSpinner from "../../../../components/SmallerSpinner.jsx"
import { createClinicPrescription } from "../../../../api/clinic/consultationApi.js"
import MedicationCard from "../../../Dispensary/Script/components/views/MedicationCard.jsx"
import GenericPopup from "../../../Dispensary/Script/views/GenericPopup.jsx"
import useGenericSubstitution from "../../../Dispensary/Script/hooks/useGenericSubstitution.js"
import ErrorModal from "../../../../components/ErrorModal"
import { useAuth } from "../../../../context/AuthContext.jsx"

export default function CreateScript({ patientName = "", patientId, consultationId, appointmentId, profileNumber, onComplete }) {
  const [medications, setMedications] = useState([])
  const [repeatScript, setRepeatScript] = useState(false)
  const [chronicMedication, setChronicMedication] = useState(false)
  const [repeatCount, setRepeatCount] = useState(2)
  const [currentPage, setCurrentPage] = useState(1)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState("")
  const [validationMessage, setValidationMessage] = useState("")

  const { user } = useAuth()
  const isPCDT = Number(user?.roleid) === 8

  const navigate = useNavigate()
  const generic = useGenericSubstitution({ medications, setMedications })

  const medicationsPerPage = 3
  const totalPages = Math.ceil(medications.length / medicationsPerPage)
  const startIndex = (currentPage - 1) * medicationsPerPage
  const endIndex = startIndex + medicationsPerPage
  const currentMedications = medications.slice(startIndex, endIndex)

  const handleAddMedication = () => {
    const newId = medications.length ? Math.max(...medications.map((m) => m.id)) + 1 : 1
    const newMedications = [
      ...medications,
      {
        id: newId,
        drugName: "",
        quantity: 1,
        packSize: "",
        strength: "",
        icd10: "",
        icd10Options: [],
        instructions: "",
        instructionsOther: "",
        sku: null,
        nappiCode: "",
        costPrice: "",
        stockText: "",
        stockQty: undefined,
        toFollowStruck: false,
        deliveryOption: "",
        saved: false,
      },
    ]
    setMedications(newMedications)
    const newTotalPages = Math.ceil(newMedications.length / medicationsPerPage)
    setCurrentPage(newTotalPages)
  }

  const handleDeleteMedication = (id) => {
    const newMedications = medications.filter((med) => med.id !== id)
    setMedications(newMedications)
    const newTotalPages = Math.ceil(newMedications.length / medicationsPerPage)
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages)
    }
  }

  const handleMedicationChange = (id, field, value) => {
    setMedications(prev => prev.map((med) => (med.id === id ? { ...med, [field]: value } : med)))
  }

  const validateLastMedication = () => {
    if (medications.length === 0) return true;
    
    const lastMed = medications[medications.length - 1];
    const hasDrugName = !!(lastMed.drugName && String(lastMed.drugName).trim() !== "");
    const hasDirections = !!(lastMed.instructions && (lastMed.instructions !== "Other" || (lastMed.instructionsOther && String(lastMed.instructionsOther).trim() !== "")));
    
    return hasDrugName && hasDirections;
  }

  const validateAllMedications = () => {
    if (medications.length === 0) {
      setValidationMessage("Please add at least one medication before completing.");
      return false;
    }

    for (const med of medications) {
      const hasDrugName = !!(med.drugName && String(med.drugName).trim() !== "");
      const hasDirections = !!(med.instructions && (med.instructions !== "Other" || (med.instructionsOther && String(med.instructionsOther).trim() !== "")));
      
      if (!hasDrugName) {
        setValidationMessage("Please enter a drug name for all medications.");
        return false;
      }
      
      if (!hasDirections) {
        setValidationMessage("Please select directions for all medications.");
        return false;
      }
    }

    return true;
  }

  const handleSaveMedication = (id) => {
    const med = medications.find((m) => m.id === id);
    if (!med) return;
    if (med.saved) {
      return;
    }
    const hasDirections = !!(med.instructions && (med.instructions !== "Other" || (med.instructionsOther && String(med.instructionsOther).trim() !== "")));
    if (!hasDirections) {
      setValidationMessage("Please select directions for this medication.");
      return;
    }
    // Mark medication as saved without showing generic popup
    setMedications((prev) => prev.map((m) => (m.id === id ? { ...m, saved: true } : m)));
  };

  // When GenericPopup closes, mark the savingMedId as saved
  const handleGenericClose = () => {
    const id = generic.savingMedId;
    if (id != null) {
      setMedications((prev) => prev.map((m) => (m.id === id ? { ...m, saved: true } : m)));
    }
    generic.closePopup();
  };

  // When user selects Replace, also mark as saved once completed
  const handleGenericReplace = async (row) => {
    const id = generic.savingMedId;
    await generic.replaceWithSku(row);
    if (id != null) {
      setMedications((prev) => prev.map((m) => (m.id === id ? { ...m, saved: true } : m)));
    }
  };

  const handleComplete = async () => {
    if (saving) return;
    
    // Validate all medications before completing
    if (!validateAllMedications()) {
      return;
    }
    
    setSaveError("");
    setSaving(true);
    try {
      const items = medications
        .map(m => {
          const effDir = (m.instructions === 'Other' ? (m.instructionsOther || '') : m.instructions) || '';
          return {
            sku: m.sku || null,
            itemName: m.drugName || "",
            quantity: Number.isFinite(m.quantity) ? m.quantity : 1,
            directions: effDir,
          };
        })
        .filter(m => (m.sku || m.itemName));

      const payload = {
        patientid: patientId,
        consultationid: consultationId,
        appointmentid: appointmentId,
        pharmacistid: user?.userid ?? user?.employeenumber ?? null,
        repeatscript: repeatScript ? (Number.isFinite(repeatCount) ? repeatCount : 0) : 0,
        chronic: isPCDT ? chronicMedication : false,
        items,
      };

      const resp = await createClinicPrescription(payload);
      if (resp?.success === false) {
        throw new Error(resp?.message || 'Failed to create prescription');
      }

      // Redirect immediately to clinic consultations after successful prescription creation
      navigate('/customer/clinic/consultations');
      if (onComplete) onComplete();
    } catch (e) {
      setSaveError(e.message || 'Failed to create prescription');
    } finally {
      setSaving(false);
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <div className={styles.container}>
      {showSuccessPopup && (
        <div className={styles.successPopup}>
          <div className={styles.successPopupContent}>
            <div className={styles.successIcon}>✓</div>
            <h3>Success!</h3>
            <p>Script has been completed successfully.</p>
          </div>
        </div>
      )}

      <div className={styles.header}>
        <h1 className={styles.title}>Prescriptions</h1>
      </div>

      <h2 className={styles.patientName}>{patientName}</h2>

      <div className={styles.medicationSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Medication</h3>
          <Button variant="primary" onClick={handleAddMedication} className={styles.addButton} disabled={!validateLastMedication()}>
            + Add
          </Button>
        </div>

        <div className={styles.medicationGrid}>
          {currentMedications.map((medication, pageIndex) => {
            const overallIndex = medications.findIndex(m => m.id === medication.id);
            return (
              <MedicationCard
                key={medication.id}
                med={medication}
                onChange={handleMedicationChange}
                onDelete={handleDeleteMedication}
                index={overallIndex}
                onSave={handleSaveMedication}
                showSaveButton={false}
              />
            );
          })}
        </div>
      </div>

      <div className={styles.repeatSection}>
        <div className={styles.repeatHeader}>
          <Checkbox
            id="repeatScript"
            label="Repeat Script"
            checked={repeatScript}
            onChange={(e) => setRepeatScript(e.target.checked)}
          />
          {isPCDT && (
            <Checkbox
              id="chronicMedication"
              label="Chronic Medication"
              checked={chronicMedication}
              onChange={(e) => setChronicMedication(e.target.checked)}
            />
          )}
        </div>

        {repeatScript && (
          <div className={styles.repeatControls}>
            <SmallerSpinner
              value={Number.isFinite(repeatCount) ? repeatCount : 1}
              min={1}
              step={1}
              ariaLabel="Repeat script count"
              compact
              onChange={(v) => setRepeatCount(v)}
            />
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
      )}

      <div className={styles.completeContainer}>
        {saveError && (
          <div className={styles.inlineError}>{saveError}</div>
        )}
        <Button variant="primary" onClick={handleComplete} className={styles.completeButton} disabled={saving}>
          {saving ? 'Completing...' : 'Complete'}
        </Button>
      </div>

      {generic.showPopup && (
        <GenericPopup
          onClose={handleGenericClose}
          title="Generic for selected item is available."
          columns={[
            { header: "Drug", accessor: "drugName" },
            { header: "Cost Price", accessor: "costPrice" },
            {
              header: "Action",
              accessor: "action",
              render: (value, row) => (
                <button
                  className={generic.replaceButtonClass}
                  onClick={() => handleGenericReplace(row)}
                >
                  Replace
                </button>
              ),
            },
          ]}
          data={generic.popupData}
        />
      )}
      <ErrorModal isOpen={!!validationMessage} onClose={() => setValidationMessage("")} message={validationMessage} />
    </div>
  )
}